// MARCA | 08/05/2026 | Shoppable media slider custom element – native scroll-snap carousel with center-active slide, drag-to-scroll, video autoplay @sections/shoppable-media-slider.liquid
import { Component } from "@theme/component";
import { CartAddEvent } from "@theme/events";

/**
 * Custom element for the shoppable media slider section.
 * Uses CSS scroll-snap for touch-friendly carousel behaviour and
 * IntersectionObserver to autoplay/pause videos as they enter/leave view.
 *
 * The center-most visible slide is marked `.is-active` and is the only
 * slide whose video autoplays. Header chevron buttons and mouse drag
 * both scroll the track.
 *
 * @extends {Component}
 */
class ShoppableMediaSlider extends Component {
  /** @type {IntersectionObserver|null} */
  #sectionObserver = null;

  /** @type {boolean} */
  #isMuted = true;

  /** @type {boolean} */
  #isPaused = false;

  /** @type {boolean} */
  #isDragging = false;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragScrollLeft = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#isMuted = this.dataset.muted !== "false";
    this.#hydrateVideos();
    this.#setupInfiniteScroll();
    this.#bindNavigation();
    this.#bindMediaControls();
    this.#bindDrag();
    this.#bindAddToCart();
    this.#setupActiveTracking();
    this.#observeSection();
  }

  disconnectedCallback() {
    this.#sectionObserver?.disconnect();
  }

  /** @returns {HTMLElement|null} */
  get #wrapper() {
    return /** @type {HTMLElement|null} */ (
      this.querySelector(".shoppable-media-slider__wrapper")
    );
  }

  /** @returns {HTMLElement[]} */
  get #slides() {
    return /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll(".shoppable-media-slider__slide"),
    ]);
  }

  /**
   * Extract deferred video elements from <template> tags into the live DOM.
   * Transfers the poster <img> src to the video's native poster attribute so
   * the browser shows it until playback begins, matching the original theme.
   */
  #hydrateVideos() {
    const templates = this.querySelectorAll(
      ".shoppable-media-slider__video-template",
    );
    templates.forEach((el) => {
      const tpl = /** @type {HTMLTemplateElement} */ (el);
      const parent = tpl.parentNode;
      if (!parent) return;

      const poster = /** @type {HTMLImageElement|null} */ (
        parent.querySelector(".shoppable-media-slider__video-poster")
      );

      while (tpl.content.firstChild) {
        parent.insertBefore(tpl.content.firstChild, tpl);
      }
      tpl.remove();

      const video = /** @type {HTMLVideoElement|null} */ (
        parent.querySelector("video")
      );
      if (!video) return;

      if (poster) {
        video.setAttribute("poster", poster.currentSrc || poster.src);
        poster.remove();
      }

      video.muted = this.#isMuted;
      video.playsInline = true;
      video.autoplay = false;
      video.pause();
    });
  }

  /**
   * Clone slides before and after the originals so the carousel can scroll
   * infinitely in both directions. After scroll settles, the position is
   * silently reset to the equivalent spot in the original set.
   */
  #setupInfiniteScroll() {
    const wrapper = this.#wrapper;
    if (!wrapper) return;

    const slides = /** @type {HTMLElement[]} */ ([
      ...wrapper.querySelectorAll(".shoppable-media-slider__slide"),
    ]);
    if (slides.length < 2) return;

    this._originalCount = slides.length;

    for (let i = 0; i < 2; i++) {
      const frag = document.createDocumentFragment();
      slides.forEach((s) => frag.appendChild(s.cloneNode(true)));
      wrapper.insertBefore(frag, wrapper.firstChild);
    }

    for (let i = 0; i < 2; i++) {
      slides.forEach((s) => wrapper.appendChild(s.cloneNode(true)));
    }

    const allSlides = /** @type {HTMLElement[]} */ ([
      ...wrapper.querySelectorAll(".shoppable-media-slider__slide"),
    ]);
    const firstOriginal = allSlides[this._originalCount * 2];
    if (!firstOriginal) return;
    wrapper.style.scrollBehavior = "auto";
    wrapper.style.scrollSnapType = "none";
    const slideCenter =
      firstOriginal.offsetLeft + firstOriginal.offsetWidth / 2;
    wrapper.scrollLeft = slideCenter - wrapper.clientWidth / 2;
    wrapper.style.scrollBehavior = "";
    wrapper.style.scrollSnapType = "";
  }

  /**
   * If the scroll position has drifted into a clone set, silently jump
   * back to the equivalent position in the original set.
   */
  #checkInfiniteLoop() {
    const wrapper = this.#wrapper;
    if (!wrapper || !this._originalCount) return;

    const n = this._originalCount;
    const allSlides = /** @type {HTMLElement[]} */ ([
      ...wrapper.querySelectorAll(".shoppable-media-slider__slide"),
    ]);
    if (allSlides.length <= n) return;

    const first = allSlides[0];
    const setStart = allSlides[n];
    const originalsEl = allSlides[n * 2];
    if (!first || !setStart || !originalsEl) return;

    const setWidth = setStart.offsetLeft - first.offsetLeft;
    const originalsStart = originalsEl.offsetLeft;
    const scrollLeft = wrapper.scrollLeft;

    let jump = 0;
    if (scrollLeft >= originalsStart + setWidth) {
      jump = -setWidth;
    } else if (scrollLeft <= originalsStart - setWidth) {
      jump = setWidth;
    }

    if (jump) {
      wrapper.style.scrollBehavior = "auto";
      wrapper.style.scrollSnapType = "none";
      wrapper.scrollLeft += jump;
      requestAnimationFrame(() => {
        wrapper.style.scrollBehavior = "";
        wrapper.style.scrollSnapType = "";
      });
    }
  }

  /**
   * Track scroll position and mark the center-most slide as active.
   * Only the active slide's video will autoplay.
   */
  #setupActiveTracking() {
    const wrapper = this.#wrapper;
    if (!wrapper) return;

    /** @type {ReturnType<typeof setTimeout>|undefined} */
    let scrollTimeout;
    const onScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.#checkInfiniteLoop();
        this.#updateActiveSlide();
      }, 80);
    };

    wrapper.addEventListener("scroll", onScroll, { passive: true });

    requestAnimationFrame(() => this.#updateActiveSlide());
  }

  /** Find the slide closest to the horizontal center and mark it active. */
  #updateActiveSlide() {
    const wrapper = this.#wrapper;
    if (!wrapper) return;

    const slides = this.#slides;
    if (!slides.length) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const wrapperCenter = wrapperRect.left + wrapperRect.width / 2;

    let closestSlide = slides[0];
    let closestDistance = Infinity;

    slides.forEach((slide) => {
      const rect = slide.getBoundingClientRect();
      const slideCenter = rect.left + rect.width / 2;
      const distance = Math.abs(slideCenter - wrapperCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestSlide = slide;
      }
    });

    slides.forEach((slide) => {
      const wasActive = slide.classList.contains("is-active");
      const isNowActive = slide === closestSlide;

      slide.classList.toggle("is-active", isNowActive);

      if (isNowActive && !wasActive) {
        this.#syncSlideIcons(slide);
      }

      const video = slide.querySelector("video");
      if (!video) return;

      if (isNowActive && !this.#isPaused) {
        video.play().catch(() => {});
      } else if (!isNowActive && wasActive) {
        video.pause();
        video.currentTime = 0;
      }
    });
  }

  /**
   * Wire the header chevron buttons to scroll the carousel by one slide width.
   */
  #bindNavigation() {
    const section = this.closest(".shopify-section");
    if (!section) return;

    const buttons = section.querySelectorAll("[data-shoppable-nav]");
    const wrapper = this.#wrapper;
    if (!wrapper) return;

    buttons.forEach((btn) => {
      btn.addEventListener("click", () => {
        const direction = /** @type {HTMLElement} */ (btn).dataset.shoppableNav;
        const slide = wrapper.querySelector(".shoppable-media-slider__slide");
        if (!slide) return;

        const slideWidth = slide.getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(wrapper).gap) || 0;
        const scrollAmount = slideWidth + gap;

        wrapper.scrollBy({
          left: direction === "previous" ? -scrollAmount : scrollAmount,
          behavior: "smooth",
        });
      });
    });
  }

  /**
   * Enable mouse drag-to-scroll on the wrapper for desktop users.
   */
  #bindDrag() {
    const wrapper = this.#wrapper;
    if (!wrapper) return;

    wrapper.addEventListener("mousedown", (/** @type {MouseEvent} */ e) => {
      this.#isDragging = true;
      this.#dragStartX = e.pageX - wrapper.offsetLeft;
      this.#dragScrollLeft = wrapper.scrollLeft;
      wrapper.style.scrollBehavior = "auto";
      wrapper.style.scrollSnapType = "none";
    });

    wrapper.addEventListener("mousemove", (/** @type {MouseEvent} */ e) => {
      if (!this.#isDragging) return;
      e.preventDefault();
      const x = e.pageX - wrapper.offsetLeft;
      const walk = (x - this.#dragStartX) * 1.2;
      wrapper.scrollLeft = this.#dragScrollLeft - walk;
    });

    const endDrag = () => {
      if (!this.#isDragging) return;
      this.#isDragging = false;
      wrapper.style.scrollBehavior = "";
      wrapper.style.scrollSnapType = "";
    };

    wrapper.addEventListener("mouseup", endDrag);
    wrapper.addEventListener("mouseleave", endDrag);
  }

  /**
   * Bind mute/unmute and play/pause overlay controls on each slide.
   */
  #bindMediaControls() {
    this.querySelectorAll(".shoppable-media-slider__control--sound").forEach(
      (btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.#isMuted = !this.#isMuted;
          this.#syncMuteState();
        });
      },
    );

    this.querySelectorAll(".shoppable-media-slider__control--playback").forEach(
      (btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.#isPaused = !this.#isPaused;
          this.#syncPlaybackState();
        });
      },
    );
  }

  /**
   * Bind quick-add-to-cart buttons. Single-variant products add directly
   * via the Cart API; multi-variant products navigate to the product page.
   */
  #bindAddToCart() {
    this.querySelectorAll(".product-slide-caption__add-to-cart-button").forEach(
      (btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const button = /** @type {HTMLButtonElement} */ (btn);
          const productUrl = button.dataset.productUrl;

          if (productUrl) {
            window.location.href = productUrl;
            return;
          }

          const variantId = button.dataset.variantId;
          if (!variantId || button.disabled) return;

          this.#addToCart(button, variantId);
        });
      },
    );
  }

  /** @param {HTMLButtonElement} button @param {string} variantId */
  async #addToCart(button, variantId) {
    button.disabled = true;
    button.classList.add("loading");

    try {
      const routes = /** @type {any} */ (window.Shopify)?.routes?.root || "/";
      const res = await fetch(`${routes}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        }),
      });

      if (!res.ok) throw new Error("Add to cart failed");
      await res.json();

      const cartRes = await fetch(`${routes}cart.js`);
      const cartData = await cartRes.json();

      document.dispatchEvent(
        new CartAddEvent(cartData, "shoppable-media-slider", {
          itemCount: cartData.item_count,
          source: "shoppable-media-slider",
        }),
      );

      button.classList.remove("loading");
      button.classList.add("added");
      setTimeout(() => {
        button.classList.remove("added");
        button.disabled = false;
      }, 1500);
    } catch {
      button.classList.remove("loading");
      setTimeout(() => {
        button.disabled = false;
      }, 1500);
    }
  }

  /** Apply mute state to all videos and update icons on a single slide only. */
  #syncMuteState() {
    this.querySelectorAll("video").forEach((video) => {
      video.muted = this.#isMuted;
    });

    const activeSlide = this.querySelector(
      ".shoppable-media-slider__slide.is-active",
    );
    if (activeSlide) this.#syncSlideIcons(activeSlide);
  }

  /** Pause or resume the active video and update icons on that slide only. */
  #syncPlaybackState() {
    const activeSlide = this.querySelector(
      ".shoppable-media-slider__slide.is-active",
    );
    const activeVideo = activeSlide?.querySelector("video");

    if (activeVideo) {
      if (this.#isPaused) {
        activeVideo.pause();
      } else {
        activeVideo.play().catch(() => {});
      }
    }

    if (activeSlide) this.#syncSlideIcons(activeSlide);
  }

  /** Update mute + playback icons on a specific slide to match global state. */
  #syncSlideIcons(/** @type {Element} */ slide) {
    const muteIcon = slide.querySelector(
      ".shoppable-media-slider__control-icon--mute",
    );
    const unmuteIcon = slide.querySelector(
      ".shoppable-media-slider__control-icon--unmute",
    );
    muteIcon?.classList.toggle("hidden", this.#isMuted);
    unmuteIcon?.classList.toggle("hidden", !this.#isMuted);

    const pauseIcon = slide.querySelector(
      ".shoppable-media-slider__control-icon--pause",
    );
    const playIcon = slide.querySelector(
      ".shoppable-media-slider__control-icon--play",
    );
    pauseIcon?.classList.toggle("hidden", this.#isPaused);
    playIcon?.classList.toggle("hidden", !this.#isPaused);
  }

  /**
   * Observe the section itself so we can pause all videos when it scrolls
   * entirely out of the viewport.
   */
  #observeSection() {
    this.#sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            this.querySelectorAll("video").forEach((v) => v.pause());
          } else {
            this.#updateActiveSlide();
          }
        });
      },
      { threshold: 0 },
    );

    this.#sectionObserver.observe(this);
  }
}

if (!customElements.get("shoppable-media-slider")) {
  customElements.define("shoppable-media-slider", ShoppableMediaSlider);
}
